import { Link, useLocation, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { applicationQueryKey, fetchApplication } from "@/lib/application-query";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useViewingOrg } from "@/contexts/viewing-org-context";
import { useAuth } from "@/contexts/auth-context";

interface StageCounts {
  all: number;
  created: number;
  in_progress: number;
  received: number;
  in_review: number;
  submitted: number;
  approved: number;
  contracts_requested: number;
  contracts_out: number;
  contracts_in: number;
  funded: number;
  declined: number;
  deal_lost: number;
}

const stages = [
  { key: "all", label: "All Applications", path: "" },
  { key: "created", label: "Created", path: "CREATED" },
  { key: "in_progress", label: "In Progress", path: "IN_PROGRESS" },
  { key: "received", label: "Received", path: "RECEIVED" },
  { key: "in_review", label: "In Review", path: "IN_REVIEW" },
  { key: "submitted", label: "Submitted", path: "SUBMITTED" },
  { key: "approved", label: "Approved", path: "APPROVED" },
  { key: "contracts_requested", label: "Contracts Requested", path: "CONTRACTS_REQUESTED" },
  { key: "contracts_out", label: "Contracts Out", path: "CONTRACTS_OUT" },
  { key: "contracts_in", label: "Contracts In", path: "CONTRACTS_IN" },
  { key: "funded", label: "Funded", path: "FUNDED" },
  { key: "declined", label: "Declined", path: "DECLINED" },
  { key: "deal_lost", label: "Deal Lost", path: "DEAL_LOST" },
];

interface StageRailProps {
  onClose?: () => void;
}

export function StageRail({ onClose }: StageRailProps) {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewingOrgId } = useViewingOrg();
  const { user } = useAuth();
  const isProgramMultiDealer =
    user?.role === "TERRITORY_SALES_MANAGER" ||
    (user?.role === "ORG_ADMIN" && user?.organization_type === "MANUFACTURER");
  const effectiveOrgId = isProgramMultiDealer
    ? viewingOrgId
    : viewingOrgId ?? user?.organization ?? null;

  const { data: counts } = useQuery({
    queryKey: ["stage-counts", effectiveOrgId],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (effectiveOrgId != null) {
        qs.set("organization_id", String(effectiveOrgId));
      }
      const url = qs.toString()
        ? `/api/applications/counts_by_stage/?${qs.toString()}`
        : "/api/applications/counts_by_stage/";
      const { data } = await customInstance.get<StageCounts>(url);
      return data;
    },
    refetchInterval: 30000,
    staleTime: 15000,
    retry: false,
  });

  const isArchivedView = searchParams.get("is_archived") === "true";

  // Share cache with application detail (dedupes full GET)
  const { data: application } = useQuery({
    queryKey: applicationQueryKey(params.id),
    queryFn: () => fetchApplication(params.id!, isArchivedView),
    enabled: !!params.id && location.pathname.startsWith("/apps/") && location.pathname !== "/apps",
    retry: false,
    select: (data) => data as { stage?: string },
  });

  // Get stage from URL params, or from application if on detail page
  const urlStage = new URLSearchParams(location.search).get("stage");
  const appStage = application?.stage;
  
  // Determine if we're on list or detail page
  const isListPage = location.pathname === "/apps";
  const isDetailPage = location.pathname.match(/^\/apps\/\d+$/);
  
  // On list page, URL stage is the source of truth (null = all applications)
  // On detail page, use application stage
  const currentStage = isListPage ? urlStage : (isDetailPage ? appStage : null);
  
  // Manage localStorage: store when stage is selected, clear when viewing all
  useEffect(() => {
    if (isListPage) {
      if (urlStage) {
        // Store stage when filtering
        localStorage.setItem("selectedStage", urlStage);
      } else {
        // Clear when viewing all applications
        localStorage.removeItem("selectedStage");
      }
    } else if (isDetailPage && appStage) {
      // Store application stage for detail pages
      localStorage.setItem("selectedStage", appStage);
    }
  }, [isListPage, isDetailPage, urlStage, appStage]);

  const handleLinkClick = () => {
    // Close drawer on mobile/tablet when a link is clicked
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="w-64 h-full bg-white border-r border-gray-200 flex flex-col lg:relative fixed left-0 top-0 bottom-0 z-50">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Stage Rail</h2>
        {/* Close button for mobile/tablet */}
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="lg:hidden"
          >
            ✕
          </Button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        {stages.map((stage) => {
          const count = counts?.[stage.key as keyof StageCounts] || 0;
          const isActive = stage.key === "all" ? !currentStage : currentStage === stage.path;

          return (
            <Link
              key={stage.key}
              to={stage.key === "all" ? "/apps" : `/apps?stage=${stage.path}`}
              onClick={(e) => {
                e.preventDefault();
                // Clear stored stage when clicking "All Applications"
                if (stage.key === "all") {
                  localStorage.removeItem("selectedStage");
                } else {
                  localStorage.setItem("selectedStage", stage.path);
                }
                // Clear all filters when changing stages (only keep stage param)
                const newParams = new URLSearchParams();
                if (stage.key !== "all") {
                  newParams.set("stage", stage.path);
                }
                navigate(`/apps?${newParams.toString()}`);
                handleLinkClick();
              }}
              className={cn(
                "flex items-center justify-between px-4 py-3 rounded-lg mb-1 transition-colors",
                isActive
                  ? "bg-blue-50 text-blue-700 font-medium"
                  : "text-gray-700 hover:bg-gray-50"
              )}
            >
              <span>{stage.label}</span>
              <span
                className={cn(
                  "text-xs font-semibold px-2 py-1 rounded-full",
                  isActive ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700"
                )}
              >
                {count}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

